// A plain code editor: a monospace textarea with a line-number gutter that scrolls with it.
// Tab inserts two spaces instead of leaving the field, so code can be indented.
import { useRef } from "react";

import { cn } from "../ui/cn";

interface Props {
  label: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
  className?: string;
}

export function CodeEditor({ label, value, onChange, rows = 14, placeholder, className }: Props) {
  const gutter = useRef<HTMLDivElement>(null);
  const lines = Math.max(1, value.split("\n").length);

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key !== "Tab" || e.shiftKey) return;
    e.preventDefault();
    const el = e.currentTarget;
    const at = el.selectionStart;
    onChange(`${value.slice(0, at)}  ${value.slice(el.selectionEnd)}`);
    requestAnimationFrame(() => el.setSelectionRange(at + 2, at + 2));
  }

  return (
    <div className={cn("flex overflow-hidden rounded-lg border border-muted bg-gray-0 focus-within:border-primary dark:bg-gray-50", className)}>
      <div
        ref={gutter}
        aria-hidden
        className="select-none overflow-hidden border-e border-muted bg-gray-50 px-2 py-2 text-end font-mono text-[12.5px] leading-5 text-gray-400 dark:bg-gray-100"
      >
        {Array.from({ length: lines }, (_, i) => (
          <div key={i}>{i + 1}</div>
        ))}
      </div>
      <textarea
        aria-label={label}
        spellCheck={false}
        rows={rows}
        value={value}
        placeholder={placeholder}
        onKeyDown={onKeyDown}
        onScroll={(e) => {
          if (gutter.current) gutter.current.scrollTop = e.currentTarget.scrollTop;
        }}
        onChange={(e) => onChange(e.target.value)}
        className="min-w-0 flex-1 resize-y whitespace-pre border-0 bg-transparent px-3 py-2 font-mono text-[12.5px] leading-5 text-gray-900 outline-none focus:ring-0"
      />
    </div>
  );
}
